import { useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { ApiError } from '@/api/client/abpError';
import { terminalRequestsApi } from '@/api/services/terminalRequests';
import type { TerminalRequestDto, TerminalRequestRefInput } from '@/api/types/terminalRequests';
import { RequestStatus } from '@/api/types/terminalRequests';
import { TerminalRequestsPermissions } from './permissions';
import { TerminalRequestDetailsModal } from './TerminalRequestDetailsModal';
import { useUpdateTerminalRequestStatus } from './api';

// App-standard DD/MM/YYYY HH:mm:ss (matches legacy + the date pickers), not the
// browser's locale format (which rendered US M/D/YYYY h:mm AM/PM).
function formatDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function statusBadge(row: TerminalRequestDto) {
  const cls =
    row.statusId === RequestStatus.Approved ? 'bg-success'
    : row.statusId === RequestStatus.Rejected ? 'bg-danger'
    : row.statusId === RequestStatus.Processing ? 'bg-info'
    : 'bg-warning text-dark'; // Pending
  return <span className={`badge ${cls}`}>{row.status}</span>;
}

function refOf(row: TerminalRequestDto): TerminalRequestRefInput {
  return { tenantId: row.tenantId ?? '', requestId: row.id };
}

type ConfirmState =
  | { row: TerminalRequestDto; newStatusId: RequestStatus; title: string; message: string; success: string }
  | null;

// Host "LP Requests Management": terminal change requests across every tenant
// with the process / approve / reject workflow (gated by the Manage permission)
// and a details dialog. Mirrors the legacy TerminalRequestsManagement page.
export function TerminalRequestsPage() {
  const can = usePermission();
  const canManage = can(TerminalRequestsPermissions.manage);

  const toast = useToast();
  const updateMut = useUpdateTerminalRequestStatus();

  const [detailsTarget, setDetailsTarget] = useState<TerminalRequestRefInput | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);

  const table = useServerDataTable<TerminalRequestDto>({
    queryKey: ['terminalRequests', 'list'],
    fetcher: (req) => terminalRequestsApi.getList(req),
    pageSize: 5,
  });

  // Column order mirrors the legacy LP Requests Management page.
  const columns: DataGridColumn<TerminalRequestDto>[] = [
    { key: 'tenantName', header: 'Client', sortable: true, filterable: true, cell: (r) => r.tenantName },
    { key: 'requestType', header: 'Type', sortable: true, cell: (r) => r.requestType },
    { key: 'userName', header: 'User', sortable: true, filterable: true, cell: (r) => r.userName },
    { key: 'requestDate', header: 'Requested', sortable: true, cell: (r) => formatDateTime(r.requestDate) },
    { key: 'status', header: 'Status', sortable: true, cell: statusBadge },
    { key: 'terminalName', header: 'Terminal', sortable: true, cell: (r) => r.terminalName },
    { key: 'connectionType', header: 'Connection Type', sortable: true, cell: (r) => r.connectionType },
  ];

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function updateStatus(row: TerminalRequestDto, newStatusId: RequestStatus, success: string) {
    updateMut.mutate(
      { ...refOf(row), newStatusId },
      {
        onSuccess: () => toast.success(success),
        onError: (err) => reportError(err, 'The action could not be completed.'),
      },
    );
  }

  function runConfirm() {
    if (!confirm) return;
    updateStatus(confirm.row, confirm.newStatusId, confirm.success);
    setConfirm(null);
  }

  function rowActions(row: TerminalRequestDto) {
    const isPending = row.statusId === RequestStatus.Pending;
    const isProcessing = row.statusId === RequestStatus.Processing;
    return (
      <div className="d-inline-flex gap-1 justify-content-center">
        <button
          type="button"
          className="btn btn-sm btn-outline-info"
          title="View details"
          onClick={() => setDetailsTarget(refOf(row))}
        >
          <i className="fa fa-eye" />
        </button>

        {canManage && isPending && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            title="Start processing"
            disabled={updateMut.isPending}
            onClick={() => updateStatus(row, RequestStatus.Processing, 'Request moved to processing.')}
          >
            <i className="fa fa-hourglass-start" />
          </button>
        )}

        {canManage && isProcessing && (
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            title="Approve"
            disabled={updateMut.isPending}
            onClick={() =>
              setConfirm({
                row,
                newStatusId: RequestStatus.Approved,
                title: 'Confirm',
                message: 'Approve this request?',
                success: 'Request approved.',
              })
            }
          >
            <i className="fa fa-check" />
          </button>
        )}

        {canManage && (isPending || isProcessing) && (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            title="Reject"
            disabled={updateMut.isPending}
            onClick={() =>
              setConfirm({
                row,
                newStatusId: RequestStatus.Rejected,
                title: 'Confirm',
                message: 'Reject this request?',
                success: 'Request rejected.',
              })
            }
          >
            <i className="fa fa-xmark" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="LP Requests Management" breadcrumb={['Home', 'LP Requests Management']} />

      <div className="card">
        <div className="card-body">
          <DataGrid
            controller={table}
            columns={columns}
            rowKey={(r) => r.id}
            emptyMessage="No requests found."
            rowActions={rowActions}
            toolbarEnd={
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                title="Reload"
                onClick={() => table.refetch()}
              >
                <i className="fa fa-refresh" />
              </button>
            }
          />
        </div>
      </div>

      <TerminalRequestDetailsModal
        show={detailsTarget !== null}
        target={detailsTarget}
        onClose={() => setDetailsTarget(null)}
      />

      <ConfirmDialog
        show={confirm !== null}
        title={confirm?.title ?? 'Confirm'}
        message={confirm?.message ?? ''}
        confirmLabel={confirm?.newStatusId === RequestStatus.Approved ? 'Approve' : 'Reject'}
        confirmVariant={confirm?.newStatusId === RequestStatus.Approved ? 'primary' : 'danger'}
        busy={updateMut.isPending}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
